import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getKBArticles } from "@/lib/firestore";
import { useAuth } from "@/lib/auth";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { detectPlatform } from "@/lib/platform";
import { ALL_PLATFORMS, CATEGORIES, Platform, PLATFORM_LABELS } from "@/types";
import type { KBArticle } from "@/types";
import { EmptyState, Spinner } from "@/components/shared";

const CATEGORY_STYLES: Record<string, { bg: string; color: string; icon: string }> = {
  "Network & VPN":    { bg: "var(--blue-l)",   color: "var(--blue)",     icon: "M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z" },
  "Hardware":         { bg: "var(--orange-l)", color: "var(--orange)",   icon: "M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4z" },
  "Account & Access": { bg: "var(--purple-l)", color: "var(--purple)",   icon: "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" },
  "Software & Apps":  { bg: "var(--green-l)",  color: "var(--green-t)",  icon: "M20 3H4v10c0 2.21 1.79 4 4 4h6c2.21 0 4-1.79 4-4v-3h2c1.11 0 2-.89 2-2V5c0-1.11-.89-2-2-2zm0 5h-2V5h2v3z" },
  "Email / M365":     { bg: "var(--blue-l)",   color: "var(--blue-t)",   icon: "M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" },
  "Other":            { bg: "var(--gray)",     color: "var(--text2)",    icon: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" },
};

const READ_TIME_PER_100_WORDS = 0.5;

export default function KnowledgeBase() {
  useDocumentTitle("Knowledge Base");
  const navigate = useNavigate();
  const { helpDeskUser } = useAuth();
  const isAgent = helpDeskUser?.role === "agent" || helpDeskUser?.role === "admin";
  const [articles, setArticles] = useState<KBArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState<string | null>(null);
  const detectedPlatform = useMemo(() => detectPlatform(), []);
  const [platformFilter, setPlatformFilter] = useState<Platform | "all">(detectedPlatform ?? "all");

  useEffect(() => {
    getKBArticles().then((a) => { setArticles(a); setLoading(false); });
  }, []);

  const filtered = articles.filter((a) => {
    if (catFilter && a.category !== catFilter) return false;
    if (platformFilter !== "all") {
      const ps = a.platforms ?? [];
      // Empty platforms means "applies to all" — always include.
      if (ps.length > 0 && !ps.includes(platformFilter)) return false;
    }
    if (search && !a.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Which platforms are actually represented in this KB so we don't
  // show pill options for OSes the team has never published for.
  const usedPlatforms = useMemo(() => {
    const set = new Set<Platform>();
    for (const a of articles) for (const p of a.platforms ?? []) set.add(p);
    return ALL_PLATFORMS.filter((p) => set.has(p));
  }, [articles]);

  const counts: Record<string, number> = {};
  for (const a of articles) counts[a.category] = (counts[a.category] ?? 0) + 1;

  if (loading) {
    return <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><Spinner size={28} /></div>;
  }

  if (articles.length === 0) {
    return (
      <div style={{ flex: 1, padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div>
            <h1 style={{ marginBottom: 3 }}>Knowledge Base</h1>
            <p style={{ color: "var(--text2)", fontSize: 13 }}>Self-service articles for common issues.</p>
          </div>
          {isAgent && (
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => navigate("/kb/import")}>📥 Import .md</button>
              <button className="btn btn-primary" onClick={() => navigate("/kb/new")}>+ New Article</button>
            </div>
          )}
        </div>
        <EmptyState icon="📚" title="No articles yet"
          subtitle={isAgent
            ? "Click '+ New Article' to publish your first one."
            : "Your team hasn't published articles yet."}
          action={isAgent
            ? { label: "+ New Article", onClick: () => navigate("/kb/new") }
            : { label: "Ask AI Assistant instead →", onClick: () => navigate("/chat") }} />
      </div>
    );
  }

  return (
    <div style={{ overflowY: "auto", flex: 1, padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <h1 style={{ marginBottom: 3 }}>Knowledge Base</h1>
          <p style={{ color: "var(--text2)", fontSize: 13 }}>Self-service articles for common issues.</p>
        </div>
        {isAgent && <button className="btn btn-primary" onClick={() => navigate("/kb/new")}>+ New Article</button>}
      </div>

      <input className="fi" style={{ marginBottom: 12 }}
        placeholder={`Search ${articles.length} articles…`} value={search} onChange={(e) => setSearch(e.target.value)} />

      {usedPlatforms.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: .4, marginRight: 4 }}>Platform</span>
          <button onClick={() => setPlatformFilter("all")}
            style={pillStyle(platformFilter === "all")}>All</button>
          {usedPlatforms.map((p) => (
            <button key={p} onClick={() => setPlatformFilter(p)}
              style={pillStyle(platformFilter === p)}>
              {PLATFORM_LABELS[p]}
              {detectedPlatform === p && <span style={{ marginLeft: 5, fontSize: 9, opacity: 0.8 }}>(yours)</span>}
            </button>
          ))}
        </div>
      )}

      {!search && (
        <>
          <h3 style={{ marginBottom: 10 }}>Browse by Category</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
            {CATEGORIES.map((label) => {
              const style = CATEGORY_STYLES[label] ?? CATEGORY_STYLES["Other"];
              return (
                <div key={label} className="card card-body"
                  onClick={() => setCatFilter(catFilter === label ? null : label)}
                  style={{ cursor: "pointer", border: catFilter === label ? `2px solid ${style.color}` : undefined, padding: 14 }}
                >
                  <div style={{ width: 30, height: 30, background: style.bg, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill={style.color}><path d={style.icon} /></svg>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
                  <div style={{ fontSize: 11, color: "var(--text2)" }}>{counts[label] ?? 0} articles</div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <h3>{catFilter ? catFilter : "Most Viewed"}</h3>
        {catFilter && <button onClick={() => setCatFilter(null)} style={{ fontSize: 12, color: "var(--blue)", background: "none", border: "none", cursor: "pointer" }}>Clear filter ×</button>}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {filtered.map((a, i) => {
          const wordCount = (a.body || "").split(/\s+/).length;
          const mins = Math.max(1, Math.round(wordCount / 200 * READ_TIME_PER_100_WORDS * 2));
          return (
            <div key={a.id} className="card"
              style={{ display: "flex", alignItems: "center", padding: "10px 12px", cursor: "pointer", gap: 10 }}
              onClick={() => navigate(`/kb/${a.id}`)}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--blue)")}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--border)")}
            >
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", minWidth: 18 }}>{i + 1}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  {a.title}
                  {(a.platforms ?? []).map((p) => (
                    <span key={p} className="badge" style={{ fontSize: 10, background: "var(--gray)", color: "var(--text2)" }}>{PLATFORM_LABELS[p]}</span>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: "var(--text2)" }}>{a.category} · {mins} min read</div>
              </div>
              {a.rating > 0 && (
                <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 10, background: "var(--green-l)", color: "var(--green-t)" }}>{a.rating.toFixed(1)} ★</span>
              )}
              <span style={{ fontSize: 11, color: "var(--text3)" }}>{(a.viewCount ?? 0).toLocaleString()} views</span>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ padding: "32px 16px", textAlign: "center", color: "var(--text2)", fontSize: 13 }}>
            No articles found for "{search}"
            <div style={{ marginTop: 10 }}>
              <button className="btn btn-gem btn-sm" onClick={() => navigate("/chat")}>Ask AI Assistant instead →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function pillStyle(active: boolean): React.CSSProperties {
  return {
    fontSize: 12, padding: "4px 11px", borderRadius: 20,
    border: `1px solid ${active ? "var(--blue)" : "var(--border-med)"}`,
    background: active ? "var(--blue)" : "var(--surface)",
    color: active ? "white" : "var(--text2)",
    cursor: "pointer", fontFamily: "var(--font)",
  };
}
